import { FC, forwardRef, useImperativeHandle, useState } from 'react';
import { Form, Modal } from 'antd';
import { isNotEmpty } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import { showInvalidFieldMsg } from '@/common/utils/ways';
import { getUserPageList } from '@/common/api/brief';
import { postFranchiseeShare } from '@/common/api/expandStore/franchisee';
import { V2Confirm } from '@/common/components/Others/V2Confirm';
import FormSearchUser from '@/common/components/Form/FormSearchUser';
import V2Form from '@/common/components/Form/V2Form';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import styles from './FranchiseeShareModal.module.less';

interface ShareModalData {
  /** 单条共享：加盟商 ID */
  franchiseeId?: number;
  /** 批量共享：加盟商 ID 列表 */
  franchiseeIds?: number[];
  franchiseeName?: string;
  affiliation2Name?: string;
  /** 批量共享：需要禁用的归属人ID（所选加盟商各自的当前归属人） */
  excludeBelongerIds?: number[];
}

interface ShareModalState {
  open: boolean;
  data: ShareModalData | null;
}

/**
 * 加盟商共享弹窗
 * @description 同一加盟商只保留一位被共享人，再次共享时由后端替换原共享关系；支持单条共享（详情页）与批量共享（列表页勾选后操作）
 */
const Share: FC<{
  /** 加盟商 ID（单条共享时传入） */
  franchiseeId?: number;
  /** 加盟商名称 */
  franchiseeName?: string;
  /** 加盟商业务别名 */
  franchiseeAlias: string;
  /** 当前被共享人姓名 */
  franchiseeAffiliation2Name?: string;
  /** 当前归属人（单条共享时为当前加盟商归属人，批量共享时由 excludeBelongerIds 传入） */
  curBelonger?: number;
  /** 共享成功回调 */
  onSuccess?: () => void;
  ref?: any;
}> = forwardRef(({
  franchiseeId,
  franchiseeName,
  franchiseeAlias,
  franchiseeAffiliation2Name = '',
  curBelonger,
  onSuccess,
}, ref) => {
  const [form] = Form.useForm();
  const [requesting, setRequesting] = useState<boolean>(false);
  const [shareModal, setShareModal] = useState<ShareModalState>({
    open: false,
    data: null,
  });

  /** 是否批量共享：由 init 时是否传入 franchiseeIds 决定 */
  const isBatch = !!shareModal.data?.franchiseeIds?.length;

  const methods = useMethods({
    /**
     * @description 将当前加盟商归属人标记为禁用，避免共享给原归属人（批量共享时禁用所选加盟商的全部原归属人）
     */
    getShareUserPageList(params: any) {
      // 单条共享禁用当前归属人；批量共享额外禁用所选加盟商各自的归属人
      const excludeBelongerIds = [curBelonger, ...(shareModal.data?.excludeBelongerIds || [])].filter((item) => isNotEmpty(item));
      return getUserPageList(params).then((res: any) => ({
        ...res,
        objectList: Array.isArray(res?.objectList) ? res.objectList.map((item: any) => ({
          ...item,
          disabled: item.disabled || excludeBelongerIds.some((belongerId) => String(item.id) === String(belongerId)),
        })) : [],
      }));
    },
    getImperativeHandle() {
      return {
        init: methods.init,
      };
    },
    /**
     * @description 打开弹窗
     * @param options.franchiseeIds 批量共享的加盟商ID列表，不传则为单条共享
     * @param options.excludeBelongerIds 批量共享时各加盟商的当前归属人ID，用于禁用共享对象
     */
    init(options?: { franchiseeIds?: number[]; excludeBelongerIds?: number[] }) {
      form.resetFields();
      const franchiseeIds = options?.franchiseeIds || [];
      const batch = franchiseeIds.length > 0;
      setShareModal({
        open: true,
        data: {
          franchiseeId,
          franchiseeName,
          affiliation2Name: franchiseeAffiliation2Name,
          franchiseeIds: batch ? franchiseeIds : undefined,
          excludeBelongerIds: batch ? (options?.excludeBelongerIds || []) : undefined,
        },
      });
    },
    close() {
      form.resetFields();
      setShareModal({
        open: false,
        data: null,
      });
    },
    confirm() {
      form.validateFields().then((values) => {
        // 批量共享需要二次确认；单条共享保持原有直接提交逻辑
        if (!isBatch) {
          methods.submitShare(values);
          return;
        }
        const affiliation2 = values?.affiliation2;
        if (!affiliation2?.value) return;
        V2Confirm({
          content: `确认将所选${shareModal.data?.franchiseeIds?.length || 0}个加盟商共享给${affiliation2?.label || ''}？共享后对方将拥有这些加盟商的操作权限`,
          onSure: () => methods.submitShare(values),
        });
      }, methods.handleValidateError);
    },
    submitShare(values: any) {
      const affiliation2 = values?.affiliation2?.value;
      const targetFranchiseeIds = shareModal.data?.franchiseeIds || [];
      const targetFranchiseeId = shareModal.data?.franchiseeId;
      if (!affiliation2 || (!targetFranchiseeId && !targetFranchiseeIds.length) || requesting) return;

      // 批量共享传 franchiseeIds，单条共享传 franchiseeId
      const params = targetFranchiseeIds.length ? {
        franchiseeIds: targetFranchiseeIds,
        affiliation2,
        share: true,
      } : {
        franchiseeId: targetFranchiseeId,
        affiliation2,
        share: true,
      };

      setRequesting(true);
      postFranchiseeShare(params).then(methods.handleShareSuccess, methods.handleShareError).finally(methods.finishShare);
    },
    handleShareSuccess() {
      V2Message.success('共享成功');
      methods.close();
      onSuccess?.();
    },
    handleShareError(error: any) {
      console.log(error);
    },
    finishShare() {
      setRequesting(false);
    },
    handleValidateError(error: any) {
      error && showInvalidFieldMsg(error.errorFields, 1);
    },
  });

  useImperativeHandle(ref, methods.getImperativeHandle);

  return (
    <Modal
      title={isBatch ? `批量共享（共${shareModal.data?.franchiseeIds?.length || 0}条）` : `共享${franchiseeAlias}：${shareModal.data?.franchiseeName || ''}`}
      width={420}
      open={shareModal.open}
      destroyOnClose
      okText='确定'
      cancelText='取消'
      confirmLoading={requesting}
      onCancel={methods.close}
      onOk={methods.confirm}
      className={styles.shareModal}
    >
      <V2Form form={form}>
        <FormSearchUser
          label={`共享给（归属人2）`}
          name='affiliation2'
          placeholder={`搜索并选择归属人（单选）`}
          required={true}
          labelInValue
          selectApi={methods.getShareUserPageList}
        />
      </V2Form>
      {shareModal.data?.affiliation2Name ? <div className={`${styles.currentShareTip} mt-12`}>
        当前已共享给：<span className='fw-600'>{shareModal.data.affiliation2Name}</span>，重新选择将替换。
      </div> : <></>}
      <div className={`${styles.ruleTip} mt-12`}>
        <span className='fw-600'>规则：</span>
        同一{franchiseeAlias}仅可共享给 1 人；重新选择将替换当前共享对象。被共享人权限跟随其自身角色，业务仍归属原归属人。
      </div>
    </Modal>
  );
});

export default Share;
